import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFireDatabase } from '@angular/fire/database';
import { AngularFireStorage } from '@angular/fire/storage';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { FileModelBuilder } from '../../views/presentations/models/file.model';

export enum StorageCategories {
    Images = 'images',
    Videos = 'videos',
    Thumbnails = 'thumbnails'
}

@Injectable()
export class StorageService {
    private logger: Logger;

    constructor(
        loggerFactory: LoggerFactory,
        private storage: AngularFireStorage,
        private db: AngularFireDatabase,
        private auth: AngularFireAuth) {
        this.logger = loggerFactory.create('StorageService');
    }

    public async upload(presentationId: string, sectionId: string, file: FileModelBuilder) {
        // Generate file id using firebase, this way the ids are generated in order, but still unique ids
        let fileId = (await this.db.database.ref().push()).key;
        await this.put(presentationId, sectionId, fileId, file.file, file.name);
        file.id = fileId;
    }

    private async put(presentationId: string, sectionId: string, fileId: string, file: File | Blob, name: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId)
            .child('sections')
            .child(sectionId)
            .child(fileId);

        try {
            await fileRef.put(file);
            let metadata = { customMetadata: { originalName: name } };
            await fileRef.updateMetadata(metadata);
        } catch (err) {
            this.logger.error('There was an error while uploading the file.', err);
            throw err;
        }
    }

    public async deletePresentation(presentationId: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId);

        try {
            await fileRef.delete();
        } catch (e) {
            if (e.code !== 'storage/object-not-found') {
                throw e;
            }
        }

        this.logger.info(`Deleted presentation ${presentationId} files.`);
    }

    public async deleteSection(presentationId: string, sectionId: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId)
            .child('sections')
            .child(sectionId);

        try {
            await fileRef.delete();
        } catch (e) {
            if (e.code !== 'storage/object-not-found') {
                throw e;
            }
        }

        this.logger.info(`Deleted presentation ${presentationId} section '${sectionId}' files.`);
    }

    public async deleteFile(presentationId: string, sectionId: string, fileId: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId)
            .child('sections')
            .child(sectionId)
            .child(fileId);

        try {
            await fileRef.delete();
        } catch (e) {
            if (e.code !== 'storage/object-not-found') {
                throw e;
            }
        }

        this.logger.info(`Deleted presentation ${presentationId} section '${sectionId}' files.`);
    }

    public async getDownloadUrl(presentationId: string, sectionId: string, fileId: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId)
            .child('sections')
            .child(sectionId)
            .child(fileId);
        return await fileRef.getDownloadURL();
    }

    public async getMetadata(presentationId: string, sectionId: string, fileId: string) {
        let userId = this.auth.auth.currentUser.uid;
        let fileRef = this.storage.storage.ref()
            .child(userId)
            .child('presentations')
            .child(presentationId)
            .child('sections')
            .child(sectionId)
            .child(fileId);
        return await fileRef.getMetadata();
    }
}



// WEBPACK FOOTER //
// ./src/app/services/database/storage.service.ts